import React from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PublicIcon from "@mui/icons-material/Public";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "../brand/BrandContext";
import { resolveBrandContacts } from "../brand/contacts";

const VALUE_ITEMS = [
  { key: "routes", icon: PublicIcon },
  { key: "fleet", icon: DirectionsBusIcon },
  { key: "booking", icon: ConfirmationNumberIcon },
  { key: "support", icon: SupportAgentIcon },
];

const DEST_KEYS = ["italy", "france", "poland", "germany", "czechia"];

/**
 * About page: brand story, destinations, values, CTAs.
 */
const AboutPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const brand = useBrand();
  const COMPANY_CONTACTS = resolveBrandContacts(brand);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "50vh", pb: 6 }}>
      {/* Brand band */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: { xs: 5, md: 7 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.12em",
              fontWeight: 700,
              opacity: 0.9,
              display: "block",
              mb: 1,
            }}
          >
            {brand.name}
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
              lineHeight: 1.25,
              mb: 1.5,
              maxWidth: 720,
            }}
          >
            {t("about.headline")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.92,
              maxWidth: 640,
              fontSize: { xs: "1rem", sm: "1.05rem" },
              lineHeight: 1.55,
            }}
          >
            {t("about.lead")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: 4, md: 5 } }}>
        {/* Who we are */}
        <Box sx={{ mb: { xs: 5, md: 6 } }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
          >
            {t("about.whoTitle")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.65, maxWidth: 680 }}
          >
            {t("about.whoBody", { brand: brand.name })}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.65, maxWidth: 680 }}
          >
            {t("about.whoBody2")}
          </Typography>
        </Box>

        {/* Destinations */}
        <Box
          sx={{
            mb: { xs: 5, md: 6 },
            py: 3,
            px: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            {t("about.destinationsTitle")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, maxWidth: 560 }}
          >
            {t("about.destinationsSubtitle")}
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={1.25}
          >
            {DEST_KEYS.map((key) => (
              <Box
                key={key}
                sx={{
                  px: 1.75,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {t(`popularTrips.countries.${key}`)}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                px: 1.75,
                py: 1,
                borderRadius: 1,
                bgcolor: "grey.50",
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {t("about.destinationsMore")}
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, maxWidth: 560 }}
          >
            {t("about.destinationsNote")}
          </Typography>
        </Box>

        {/* Values — rows, not marketing cards */}
        <Box sx={{ mb: { xs: 5, md: 6 } }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 700, mb: 2.5, color: "text.primary" }}
          >
            {t("about.valuesTitle")}
          </Typography>
          <Stack spacing={0} divider={null}>
            {VALUE_ITEMS.map(({ key, icon: Icon }, index) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  gap: 2,
                  py: 2.5,
                  borderTop: index === 0 ? "1px solid" : undefined,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                    {t(`about.values.${key}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    {t(`about.values.${key}.body`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* CTA */}
        <Box
          sx={{
            py: { xs: 3.5, md: 4 },
            px: { xs: 2.5, sm: 3.5 },
            borderRadius: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            textAlign: { xs: "left", sm: "center" },
          }}
        >
          <Typography variant="h5" fontWeight={700} mb={1}>
            {t("about.ctaTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2.5, opacity: 0.92, maxWidth: 520, mx: { sm: "auto" } }}
          >
            {t("about.ctaBody")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={() => navigate("/")}
              sx={{
                fontWeight: 700,
                bgcolor: "common.white",
                color: "primary.main",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              {t("about.ctaSearch")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href={`tel:${COMPANY_CONTACTS.primaryPhoneTel}`}
              sx={{
                fontWeight: 600,
                borderColor: "rgba(255,255,255,0.55)",
                color: "inherit",
                "&:hover": {
                  borderColor: "common.white",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {t("about.ctaContact")}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage;
